import React from "react";
import { Link } from "react-router-dom";
import siteConfig from "../Datas/siteConfig";
import "../css/delivery.scss";

const shippingHighlights = [
  { label: "Studio dispatch", value: "1–3 business days" },
  { label: "Delivery", value: "2–10 business days" },
  { label: "Flat-rate shipping", value: siteConfig.currencySymbol + siteConfig.defaultShipping },
];

const shippingZones = [
  {
    destination: "Hong Kong",
    estimate: "2–4 business days",
    cost: siteConfig.currencySymbol + siteConfig.defaultShipping,
  },
  {
    destination: "Asia-Pacific destinations",
    estimate: "3–7 business days",
    cost: siteConfig.currencySymbol + siteConfig.defaultShipping,
  },
  {
    destination: "United Kingdom",
    estimate: "4–7 business days",
    cost: siteConfig.currencySymbol + siteConfig.defaultShipping,
  },
  { destination: "Other available destinations", estimate: "5–10 business days", cost: siteConfig.currencySymbol + siteConfig.defaultShipping },
];

const shippingSections = [
  {
    id: "preparation",
    title: "Order preparation",
    body: [
      "Orders are prepared Monday to Friday, excluding local public holidays. Ready-to-ship pieces normally leave the studio within one to three business days.",
      "Made-to-order or personalised items may need additional studio time. Any extended lead time is shown on the product page before you add the piece to your bag.",
    ],
  },
  {
    id: "rates",
    title: "Rates & estimates",
    body: [
      "The guide below shows typical standard-delivery windows after dispatch. Your final available service and price are calculated from the delivery address at checkout.",
    ],
    showRates: true,
  },
  {
    id: "tracking",
    title: "Tracking your parcel",
    body: [
      "As soon as your order is collected, we send a dispatch email with a tracking link. Tracking can take up to 24 hours to show its first scan.",
      "During launches, holidays or severe weather, carriers may need a little longer. Business-day estimates do not include weekends, customs reviews or local holidays.",
    ],
  },
  {
    id: "customs",
    title: "Destination charges",
    body: [
      "Orders delivered outside Hong Kong may be assessed local duties, taxes or handling fees by the destination authority. Unless checkout explicitly says otherwise, these charges are paid by the recipient and are not included in the order total.",
      "Border procedures vary by location and can add time to delivery. We cannot undervalue an order or mark a commercial purchase as a gift.",
    ],
  },
  {
    id: "changes",
    title: "Address changes & delivery issues",
    body: [
      "Contact us promptly if an address needs correcting. We will help whenever possible, but an order cannot be rerouted once the carrier has accepted it.",
      "If tracking says delivered but the parcel is missing, check safe places and neighbours first, then contact us within five days. Keep any damaged packaging while we open a carrier enquiry.",
    ],
    contact: true,
  },
  {
    id: "packaging",
    title: "Our packaging",
    body: [
      "Pieces are wrapped to protect their shape while keeping materials considered. Where practical, we use recyclable paper, right-sized boxes and minimal plastic. Please reuse or recycle the packaging available in your area.",
    ],
  },
];

function Delivery() {
  return (
    <main className="delivery policy-page" id="main-content">
      <header className="policy-page__header">
        <p className="policy-page__eyebrow">Customer care</p>
        <h1>Shipping</h1>
        <p>
          From our studio to your door. Find preparation times, delivery
          estimates and practical guidance for following your order.
        </p>
      </header>

      <dl className="policy-page__summary" aria-label="Shipping at a glance">
        {shippingHighlights.map((item) => (
          <div key={item.label}>
            <dt>{item.label}</dt>
            <dd>{item.value}</dd>
          </div>
        ))}
      </dl>

      <div className="policy-page__layout">
        <aside className="policy-page__aside" aria-label="On this page">
          <p>On this page</p>
          <ol>
            {shippingSections.map((section) => (
              <li key={section.id}>
                <a href={"#" + section.id}>{section.title}</a>
              </li>
            ))}
          </ol>
          <div className="policy-page__help">
            <span>Still need help?</span>
            <Link to="/contact">Contact the studio</Link>
          </div>
        </aside>

        <article className="policy-page__content">
          {shippingSections.map((section, index) => (
            <section id={section.id} key={section.id}>
              <span className="policy-page__number" aria-hidden="true">
                {String(index + 1).padStart(2, "0")}
              </span>
              <div>
                <h2>{section.title}</h2>
                {section.body.map((paragraph) => (
                  <p key={paragraph}>{paragraph}</p>
                ))}

                {section.showRates && (
                  <div
                    className="policy-page__table-wrap"
                    role="region"
                    aria-label="Shipping rates table"
                    tabIndex="0"
                  >
                    <table>
                      <caption className="policy-page__sr-only">
                        Estimated standard shipping times and costs
                      </caption>
                      <thead>
                        <tr>
                          <th scope="col">Destination</th>
                          <th scope="col">Estimated transit</th>
                          <th scope="col">From</th>
                        </tr>
                      </thead>
                      <tbody>
                        {shippingZones.map((zone) => (
                          <tr key={zone.destination}>
                            <th scope="row">{zone.destination}</th>
                            <td>{zone.estimate}</td>
                            <td>{zone.cost}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    <p className="policy-page__fine-print">
                      Estimates begin after dispatch and are not delivery guarantees.
                    </p>
                  </div>
                )}

                {section.contact && (
                  <p>
                    Email{" "}
                    <a
                      href={
                        "mailto:" + siteConfig.contact.customerServiceEmail
                      }
                    >
                      {siteConfig.contact.customerServiceEmail}
                    </a>{" "}
                    with your order number and delivery postcode.
                  </p>
                )}
              </div>
            </section>
          ))}
        </article>
      </div>
    </main>
  );
}

export default Delivery;
